import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ArrowLeft, AlertTriangle, FileCheck, Loader2 } from "lucide-react";
import { DeficiencyDetailView } from "../libraries/DeficiencyDetailView";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { apiService } from "@/services/api-service";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Checkbox } from "../ui/checkbox";

type ControlSignoffSection = "prep_so" | "rev_so" | "conc_so" | "eqr_so" | "partner_so";

interface WorkspaceDeficienciesProps {
  workspaceName: string;
  onBack?: () => void;
}

interface WorkspaceDeficiency {
  id: string;
  control_relevance: string;
  control_id: string[];
  deficiency_type: string;
  description: string | null;
  root_cause: string | null;
  signoff: {
    prep_so: Array<{ name: string; metadata: string }>;
    rev_so: Array<{ name: string; metadata: string }>;
    conc_so: Array<{ name: string; metadata: string }>;
    eqr_so: Array<{ name: string; metadata: string }>;
    partner_so: Array<{ name: string; metadata: string }>;
  };
}

function getInitialsForSignoff(name: string): string {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function ControlSignoffBadge({
  label,
  signoffArray,
  onSignoffClick,
  disabled,
}: {
  label: string;
  signoffArray: Array<{ name: string; metadata: string }>;
  onSignoffClick: () => void;
  disabled: boolean;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const validEntries = (signoffArray || []).filter((e) => e?.name && String(e.name).trim() !== "");
  const hasEntries = validEntries.length > 0;
  const formatDate = (s: string) => {
    if (!s) return "";
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };
  return (
    <div className="relative inline-flex items-center gap-1">
      {hasEntries && (
        <div
          className="flex items-center gap-0.5"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {validEntries.slice(0, 3).map((entry, idx) => (
            <div
              key={idx}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium cursor-help border border-blue-500/30"
              title={`${entry.name} - ${formatDate(entry.metadata || "")}`}
            >
              {getInitialsForSignoff(entry.name)}
            </div>
          ))}
          {validEntries.length > 3 && (
            <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
              +{validEntries.length - 3}
            </div>
          )}
        </div>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={onSignoffClick}
        disabled={disabled}
        className={`h-6 w-6 p-0 ${disabled ? "text-white/30 cursor-not-allowed" : "text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"}`}
        title={disabled ? `You don't have permission to add ${label} signoff` : `Add ${label} signoff`}
      >
        <FileCheck className="h-3 w-3" />
      </Button>
      {showTooltip && hasEntries && (
        <div className="absolute z-10 bottom-full left-1/2 mb-2 min-w-[160px] max-w-xs -translate-x-1/2 rounded border border-white/10 bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
          <div className="font-semibold mb-1">{label} ({validEntries.length})</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {validEntries.map((entry, idx) => (
              <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
                <div className="font-medium">{entry.name}</div>
                {entry.metadata && <div className="text-white/70 text-[10px]">{formatDate(entry.metadata)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const signoffDefault = {
  prep_so: [] as Array<{ name: string; metadata: string }>,
  rev_so: [] as Array<{ name: string; metadata: string }>,
  conc_so: [] as Array<{ name: string; metadata: string }>,
  eqr_so: [] as Array<{ name: string; metadata: string }>,
  partner_so: [] as Array<{ name: string; metadata: string }>,
};

export default function WorkspaceDeficiencies({
  workspaceName,
  onBack,
}: WorkspaceDeficienciesProps) {
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();

  const [workspaceDeficiencies, setWorkspaceDeficiencies] = useState<
    WorkspaceDeficiency[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [deficiencySignoffLoading, setDeficiencySignoffLoading] = useState(false);
  const [selectedDeficienciesForSignoff, setSelectedDeficienciesForSignoff] = useState<Set<string>>(new Set());
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [activeDeficiencyId, setActiveDeficiencyId] = useState<string | null>(
    null,
  );

  const loadWorkspaceDeficiencies = async () => {
    try {
      setLoading(true);
      setError("");

      const sp: any = (window as any).sharePointAPI;
      if (!sp?.downloadDeficiencyJson || !sp?.downloadRcmJson) {
        setWorkspaceDeficiencies([]);
        return;
      }

      const [defRes, rcmRes] = await Promise.all([
        sp.downloadDeficiencyJson(),
        sp.downloadRcmJson(),
      ]);

      if (!defRes?.success || !defRes.data) {
        setWorkspaceDeficiencies([]);
        return;
      }

      const rawDef = defRes.data.libraries_deficiency || {};
      const allDeficiencies: WorkspaceDeficiency[] = Object.entries(rawDef).map(
        ([id, value]: [string, any]) => {
          const signoff = value.signoff || {};
          return {
            id,
            control_relevance: value.control_relevance || "",
            control_id: Array.isArray(value.control_id) ? value.control_id : [],
            deficiency_type: value.deficiency_type || "",
            description: value.description ?? null,
            root_cause: value.root_cause ?? null,
            signoff: {
              prep_so: Array.isArray(signoff.prep_so) ? signoff.prep_so : signoffDefault.prep_so,
              rev_so: Array.isArray(signoff.rev_so) ? signoff.rev_so : signoffDefault.rev_so,
              conc_so: Array.isArray(signoff.conc_so) ? signoff.conc_so : signoffDefault.conc_so,
              eqr_so: Array.isArray(signoff.eqr_so) ? signoff.eqr_so : signoffDefault.eqr_so,
              partner_so: Array.isArray(signoff.partner_so) ? signoff.partner_so : signoffDefault.partner_so,
            },
          };
        },
      );

      if (!rcmRes?.success || !rcmRes.data) {
        setWorkspaceDeficiencies([]);
        return;
      }

      const rcmData = rcmRes.data;
      const librariesRcm = rcmData.libraries_RCM || rcmData.libraries_rcm || {};

      // Derive controls linked to this workspace
      const workspaceControls: Array<{
        controlId: string;
        controlName: string;
        typeOfControl: string;
        nature: string;
        workspace: string[];
      }> = Object.entries(librariesRcm as Record<string, any>)
        .map(([controlId, entry]) => {
          const rawWorkspace = entry.workspace || [];
          const wsArr = Array.isArray(rawWorkspace)
            ? rawWorkspace
            : rawWorkspace
            ? [rawWorkspace]
            : [];

          return {
            controlId,
            controlName: entry.control_name || entry.controlName || controlId,
            typeOfControl: entry.TypeOfControl || entry.typeOfControl || "Unknown",
            nature: entry.Nature || entry.subtype || "Unknown",
            workspace: wsArr,
          };
        })
        .filter((c) => c.workspace.includes(workspaceName));

      const workspaceControlIds = new Set(
        workspaceControls.map((c) => String(c.controlId)),
      );

      const filtered = allDeficiencies.filter((def) =>
        (def.control_id || []).some((cid) =>
          workspaceControlIds.has(String(cid)),
        ),
      );

      setWorkspaceDeficiencies(filtered);
    } catch (e: any) {
      setError(
        e?.message || "Unexpected error while loading workspace deficiencies",
      );
      setWorkspaceDeficiencies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspaceDeficiencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceName]);

  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(currentUserMember?.role ?? null);
      } catch (err) {
        console.error("Error loading user engagement role for deficiency signoff:", err);
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  const canControlSignoffSection = (section: ControlSignoffSection): boolean => {
    if (!user) return false;
    const engagementRole = userEngagementRole;
    const userType = user.type;
    switch (section) {
      case "prep_so":
        return (
          engagementRole === "associate" ||
          engagementRole === "article" ||
          userType === "associate" ||
          userType === "article"
        );
      case "rev_so":
        return (
          engagementRole === "engagement_manager" ||
          userType === "manager" ||
          userType === "associate" ||
          engagementRole === "associate"
        );
      case "conc_so":
        return userType === "manager" || userType === "partner";
      case "eqr_so":
        return engagementRole === "eqr_partner" || engagementRole === "eqr_manager";
      case "partner_so":
        return engagementRole === "engagement_partner";
      default:
        return false;
    }
  };

  const isDeficiencyPrepCompleted = (signoff: WorkspaceDeficiency["signoff"]): boolean => {
    if (!signoff || !Array.isArray(signoff.prep_so)) return false;
    return signoff.prep_so.some((entry) => entry?.name && String(entry.name).trim() !== "");
  };

  const handleDeficiencySignoff = async (deficiencyId: string, section: ControlSignoffSection) => {
    if (!user || !user.user_name) return;
    try {
      setDeficiencySignoffLoading(true);
      if (
        !(window as any).sharePointAPI?.downloadDeficiencyJson ||
        !(window as any).sharePointAPI?.uploadDeficiencyJson
      ) {
        alert("SharePoint Deficiency APIs are not available.");
        return;
      }
      const defResult = await (window as any).sharePointAPI.downloadDeficiencyJson();
      if (!defResult?.success || !defResult.data) {
        alert(defResult?.error || "Failed to load Libraries_Deficiency.json");
        return;
      }
      const defData = defResult.data;
      if (!defData.libraries_deficiency) defData.libraries_deficiency = {};
      if (!defData.libraries_deficiency[deficiencyId]) {
        alert("Deficiency not found in Libraries_Deficiency.json");
        return;
      }
      const entry = defData.libraries_deficiency[deficiencyId];
      if (!entry.signoff) {
        entry.signoff = {
          prep_so: [],
          rev_so: [],
          conc_so: [],
          eqr_so: [],
          partner_so: [],
        };
      }
      const signoffArr: any[] = Array.isArray(entry.signoff[section]) ? entry.signoff[section] : [];
      signoffArr.push({ name: user.user_name, metadata: new Date().toISOString() });
      entry.signoff[section] = signoffArr;

      const uploadResult = await (window as any).sharePointAPI.uploadDeficiencyJson(defData);
      if (!uploadResult?.success) {
        alert(uploadResult?.error || "Failed to save deficiency signoff");
        return;
      }
      await loadWorkspaceDeficiencies();
    } catch (e: any) {
      console.error("Error updating deficiency signoff:", e);
      alert(e?.message || "Unexpected error while updating deficiency signoff");
    } finally {
      setDeficiencySignoffLoading(false);
    }
  };

  const handleBulkDeficiencySignoff = async (section: ControlSignoffSection) => {
    if (!user?.user_name || selectedDeficienciesForSignoff.size === 0) return;
    const ids = Array.from(selectedDeficienciesForSignoff);
    try {
      setDeficiencySignoffLoading(true);
      for (const id of ids) {
        await handleDeficiencySignoff(id, section);
      }
      setSelectedDeficienciesForSignoff(new Set());
    } finally {
      setDeficiencySignoffLoading(false);
    }
  };

  if (activeDeficiencyId) {
    return (
      <div className="space-y-6 text-white">
        <DeficiencyDetailView
          deficiencyId={activeDeficiencyId}
          onBack={() => setActiveDeficiencyId(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 text-white">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            Deficiencies – {workspaceName}
          </h2>
          <p className="text-sm text-white/60">
            Deficiencies linked to this workspace via controls.
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        )}
      </div>

      {loading ? (
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              Loading deficiencies from SharePoint...
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-red-500/40 bg-red-500/10">
          <CardContent className="flex items-center gap-2 p-4 text-sm text-red-200">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </CardContent>
        </Card>
      ) : workspaceDeficiencies.length === 0 ? (
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6 text-center text-white/60">
            <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-white/40" />
            <p className="text-sm">
              No deficiencies are currently linked to this workspace.
            </p>
            <p className="mt-1 text-xs">
              Deficiencies will appear here when they are linked to controls
              that are associated with &quot;{workspaceName}&quot;.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-xs">
              {workspaceDeficiencies.length} deficiency(ies)
            </Badge>
            {!isArchivalMode && selectedDeficienciesForSignoff.size > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={deficiencySignoffLoading}
                    className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                  >
                    {deficiencySignoffLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileCheck className="h-4 w-4 mr-2" />
                    )}
                    Sign off on selected ({selectedDeficienciesForSignoff.size})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                  {(() => {
                    const selected = Array.from(selectedDeficienciesForSignoff);
                    const allHavePrep = selected.length > 0 && selected.every((id) => {
                      const d = workspaceDeficiencies.find((x) => x.id === id);
                      return d && isDeficiencyPrepCompleted(d.signoff || signoffDefault);
                    });
                    const canPrep = canControlSignoffSection("prep_so");
                    const canRev = canControlSignoffSection("rev_so") && allHavePrep;
                    const canConc = canControlSignoffSection("conc_so") && allHavePrep;
                    const canEqr = canControlSignoffSection("eqr_so") && allHavePrep;
                    const canPartner = canControlSignoffSection("partner_so") && allHavePrep;
                    return (
                      <>
                        <DropdownMenuItem disabled={!canPrep} onClick={() => canPrep && handleBulkDeficiencySignoff("prep_so")}>Preparer</DropdownMenuItem>
                        <DropdownMenuItem disabled={!canRev} onClick={() => canRev && handleBulkDeficiencySignoff("rev_so")}>Reviewer</DropdownMenuItem>
                        <DropdownMenuItem disabled={!canConc} onClick={() => canConc && handleBulkDeficiencySignoff("conc_so")}>Concurrent</DropdownMenuItem>
                        <DropdownMenuItem disabled={!canEqr} onClick={() => canEqr && handleBulkDeficiencySignoff("eqr_so")}>EQR</DropdownMenuItem>
                        <DropdownMenuItem disabled={!canPartner} onClick={() => canPartner && handleBulkDeficiencySignoff("partner_so")}>Partner</DropdownMenuItem>
                      </>
                    );
                  })()}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {!deficiencySignoffLoading && (
            <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
              {!isArchivalMode && (
                <div className="w-10 shrink-0 flex items-center justify-center">
                  <Checkbox
                    checked={workspaceDeficiencies.length > 0 && workspaceDeficiencies.every((d) => selectedDeficienciesForSignoff.has(d.id))}
                    onCheckedChange={() => {
                      if (workspaceDeficiencies.length > 0 && workspaceDeficiencies.every((d) => selectedDeficienciesForSignoff.has(d.id))) {
                        setSelectedDeficienciesForSignoff(new Set());
                      } else {
                        setSelectedDeficienciesForSignoff(new Set(workspaceDeficiencies.map((d) => d.id)));
                      }
                    }}
                    aria-label="Select all deficiencies"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">
                Deficiency
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="w-24 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
                <div className="w-24 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
                <div className="w-24 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
                <div className="w-24 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
                <div className="w-24 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
              </div>
              <div className="w-8 flex-shrink-0" />
            </div>
          )}
          {workspaceDeficiencies.map((def) => {
            const signoff = def.signoff || signoffDefault;
            const prepCompleted = isDeficiencyPrepCompleted(signoff);
            const canPrep = canControlSignoffSection("prep_so");
            const canRev = canControlSignoffSection("rev_so") && prepCompleted;
            const canConc = canControlSignoffSection("conc_so") && prepCompleted;
            const canEqr = canControlSignoffSection("eqr_so") && prepCompleted;
            const canPartner = canControlSignoffSection("partner_so") && prepCompleted;

            return (
              <div
                key={def.id}
                className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 p-3 hover:border-white/20 hover:bg-white/10"
              >
                {!isArchivalMode && (
                  <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedDeficienciesForSignoff.has(def.id)}
                      onCheckedChange={() => {
                        setSelectedDeficienciesForSignoff((prev) => {
                          const next = new Set(prev);
                          if (next.has(def.id)) next.delete(def.id);
                          else next.add(def.id);
                          return next;
                        });
                      }}
                      aria-label={`Select ${def.id}`}
                    />
                  </div>
                )}
                <button
                  type="button"
                  className="flex-1 min-w-0 text-left"
                  onClick={() => setActiveDeficiencyId(def.id)}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-white truncate">
                      {def.id}
                    </span>
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {def.deficiency_type || "Deficiency"}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      Relevant: {def.control_relevance || "N/A"}
                    </Badge>
                  </div>
                  {def.control_id && def.control_id.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {def.control_id.slice(0, 3).map((cid) => (
                        <Badge key={cid} variant="outline" className="text-[9px]">
                          {cid}
                        </Badge>
                      ))}
                      {def.control_id.length > 3 && (
                        <span className="text-[9px] text-white/50">+{def.control_id.length - 3}</span>
                      )}
                    </div>
                  )}
                  {def.description && (
                    <p className="text-xs text-white/80 truncate mt-0.5 line-clamp-2">
                      <span dangerouslySetInnerHTML={{ __html: def.description }} />
                    </p>
                  )}
                </button>
                {!deficiencySignoffLoading && (
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {isArchivalMode ? (
                      <>
                        <div className="w-24 flex justify-center">
                          {signoff.prep_so?.length > 0 && (
                            <div className="flex items-center gap-0.5">
                              {signoff.prep_so.slice(0, 3).map((entry: any, i: number) => (
                                <div
                                  key={i}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                  title={entry.metadata}
                                >
                                  {getInitialsForSignoff(entry.name)}
                                </div>
                              ))}
                              {signoff.prep_so.length > 3 && (
                                <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                  +{signoff.prep_so.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="w-24 flex justify-center">
                          {signoff.rev_so?.length > 0 && (
                            <div className="flex items-center gap-0.5">
                              {signoff.rev_so.slice(0, 3).map((entry: any, i: number) => (
                                <div
                                  key={i}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                  title={entry.metadata}
                                >
                                  {getInitialsForSignoff(entry.name)}
                                </div>
                              ))}
                              {signoff.rev_so.length > 3 && (
                                <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                  +{signoff.rev_so.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="w-24 flex justify-center">
                          {signoff.conc_so?.length > 0 && (
                            <div className="flex items-center gap-0.5">
                              {signoff.conc_so.slice(0, 3).map((entry: any, i: number) => (
                                <div
                                  key={i}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                  title={entry.metadata}
                                >
                                  {getInitialsForSignoff(entry.name)}
                                </div>
                              ))}
                              {signoff.conc_so.length > 3 && (
                                <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                  +{signoff.conc_so.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="w-24 flex justify-center">
                          {signoff.eqr_so?.length > 0 && (
                            <div className="flex items-center gap-0.5">
                              {signoff.eqr_so.slice(0, 3).map((entry: any, i: number) => (
                                <div
                                  key={i}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                  title={entry.metadata}
                                >
                                  {getInitialsForSignoff(entry.name)}
                                </div>
                              ))}
                              {signoff.eqr_so.length > 3 && (
                                <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                  +{signoff.eqr_so.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="w-24 flex justify-center">
                          {signoff.partner_so?.length > 0 && (
                            <div className="flex items-center gap-0.5">
                              {signoff.partner_so.slice(0, 3).map((entry: any, i: number) => (
                                <div
                                  key={i}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                  title={entry.metadata}
                                >
                                  {getInitialsForSignoff(entry.name)}
                                </div>
                              ))}
                              {signoff.partner_so.length > 3 && (
                                <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                  +{signoff.partner_so.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-24 flex justify-center">
                          <ControlSignoffBadge
                            label="Preparer"
                            signoffArray={signoff.prep_so || []}
                            onSignoffClick={() => handleDeficiencySignoff(def.id, "prep_so")}
                            disabled={!canPrep}
                          />
                        </div>
                        <div className="w-24 flex justify-center">
                          <ControlSignoffBadge
                            label="Reviewer"
                            signoffArray={signoff.rev_so || []}
                            onSignoffClick={() => handleDeficiencySignoff(def.id, "rev_so")}
                            disabled={!canRev}
                          />
                        </div>
                        <div className="w-24 flex justify-center">
                          <ControlSignoffBadge
                            label="Concurrent"
                            signoffArray={signoff.conc_so || []}
                            onSignoffClick={() => handleDeficiencySignoff(def.id, "conc_so")}
                            disabled={!canConc}
                          />
                        </div>
                        <div className="w-24 flex justify-center">
                          <ControlSignoffBadge
                            label="EQR"
                            signoffArray={signoff.eqr_so || []}
                            onSignoffClick={() => handleDeficiencySignoff(def.id, "eqr_so")}
                            disabled={!canEqr}
                          />
                        </div>
                        <div className="w-24 flex justify-center">
                          <ControlSignoffBadge
                            label="Partner"
                            signoffArray={signoff.partner_so || []}
                            onSignoffClick={() => handleDeficiencySignoff(def.id, "partner_so")}
                            disabled={!canPartner}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div className="w-8 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

